import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MergeTypeIcon from '@mui/icons-material/MergeType';
import PublicIcon from '@mui/icons-material/Public';
import {
  Box,
  Button,
  ButtonGroup,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useNavigate } from "react-router-dom";
import ConfirmationDialog from "../components/ConfirmationDialog";
import LazyLoader from "../components/LazyLoader";
import MergeList from "../components/MergeList";
import SearchField from "../components/SearchField";
import useSnackbar from "../components/snackbar/useSnackBar";
import { useApi } from "../hooks/useApi";
import { useLanguage } from "../hooks/useLanguage";

export default function Object({ userInfo, publicFlag, allFlag, changeTab, tab }) {
  const [objects, setObjects] = useState([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [endReached, setEndReached] = useState(false);
  const [query, setQuery] = useState("");

  const [updateCounter, setUpdateCounter] = useState(1);

  // state for deletion-dialog
  const [isOpen, setIsOpen] = useState({ state: false, id: null });

  // state for merge-dialog
  const [isOpenMerge, setIsOpenMerge] = useState({ state: false, id: null, name: null });

  const { locale } = useLanguage();
  const intl = useIntl();
  const api = useApi();

  const navigate = useNavigate();

  /**
   * Whenever the page is changed.
   */
  useEffect(() => {
    // do not fetch the first page here
    if (page === 1 || endReached) return;
    fetchPage(page, query);
  }, [page]);

  /**
   * Start new fetch from page 1.
   */
  useEffect(() => {
    setPage(1);
    fetchPage(1, query);
  }, [updateCounter, query, locale]);

  /**
   * Fetches the specified page with query.
   * @param {*} page 
   * @param {*} query
   */
  const fetchPage = (page, query) => {
    let fetchURL = `/object?page=${page - 1}&size=20&published=${publicFlag}&all=${allFlag}&lang=${locale}`;
    if (query) {
      fetchURL += `&search=${query}`;
    }
    setIsLoading(true);
    api
      .get(fetchURL)
      .then(response => {
        if (page === 1) {
          setObjects(response.data.content);
        } else {
          setObjects(old => [...old, ...response.data.content]);
        }
        setEndReached(response.data.last);
      })
      .catch(error => {
        console.log(error);
        if (error?.response?.status === 403) {
          navigate('/login');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  const handleSearch = (value) => {
    setQuery(value);
  };

  // open deletion-dialog
  const handleOpen = (id) => {
    setIsOpen({ state: true, id: id });
  };

  // close deletion-dialog
  const handleClose = () => {
    setIsOpen({ state: false, id: null });
  };

  /**
   * Open the merge-dialog.
   * @param {*} id 
   * @param {*} name 
   */
  const handleOpenMerge = (id, name) => {
    setIsOpenMerge({ state: true, id, name });
  };

  /**
   * Close the merge-dialog.
   */
  const handleCloseMerge = () => {
    setIsOpenMerge({ state: false, id: null, name: null });
  }

  // snackbar for when deletion is not successful
  const { openSnackBar, closeSnackBar } = useSnackbar();

  /**
   * Deletes the specified object.
   * @param {*} id 
   */
  const handleDelete = (id) => {
    api
      .delete(`/object/${id}`)
      .then((response) => {
        // handle the response
        handleClose();
        setUpdateCounter(prev => prev + 1);
      })
      .catch((error) => {
        // handle errors
        console.log(error);
        handleClose();
        openSnackBar(intl.formatMessage({ id: "Overview.deletionFailure" }));
      });
  }

  /**
   * Makes the specified object public and goes to the public-page.
   * @param {*} id 
   */
  const handleMakePublic = (id) => {
    api
      .put(`/object/publish/${id}`)
      .then(response => {
        changeTab('public');
      })
      .catch(error => {
        console.log(error);
        openSnackBar(intl.formatMessage({ id: "Object.publishFailure" }));
      })
  };

  /**
   * Merges the specified objects and reloads the objects.
   * @param {*} aid 
   * @param {*} bid 
   */
  const handleMerge = (aid, bid) => {
    api
      .put(`/object/merge/${aid}/${bid}`)
      .then(response => {
        setUpdateCounter(old => old + 1);
      })
      .catch(error => {
        console.log(error);
        openSnackBar(intl.formatMessage({ id: "Object.mergeFailure" }));
      })
      .finally(() => {
        handleCloseMerge();
      });
  };

  // dialog that prompts deletion
  const dialog = useMemo(() => {
    let d = (
      <ConfirmationDialog
        title={intl.formatMessage({ id: "Object.deletionQuestion" })}
        content={intl.formatMessage({ id: "Overview.deletionInfo" })}
        isOpen={isOpen.state}
        handleAccept={() => handleDelete(isOpen.id)}
        handleClose={handleClose}
      />
    );
    if (isOpen.state) {
      return d;
    }
  }, [isOpen]);

  /**
   * Dialog that prompts merging objects.
   */
  const mergeDialog = useMemo(() => {
    if (!isOpenMerge.state) return;
    return <MergeList
      title={isOpenMerge.name + " " + intl.formatMessage({ id: "Object.mergeWith" })}
      isOpen={isOpenMerge.state}
      handleClose={handleCloseMerge}
      handleAccept={(value) => handleMerge(isOpenMerge.id, value)}
      initialID={isOpenMerge.id}
      optionsURL={`/object?published=true&all=false${isOpenMerge.id >= 100000 ? '&showNonDefault=true' : ''}`}
      fieldName="name"
    />;
  }, [isOpenMerge]);

  // list of objects
  const objectList = useMemo(() => {
    return objects.map((object) => {
      return (
        <TableRow
          key={object.id}
          sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
        >
          {
            userInfo.isAdmin && <TableCell component="th" scope="row" sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            }} >
              {object.id}
            </TableCell>
          }
          <TableCell component="th" scope="row">
            {object.name}
          </TableCell>
          <TableCell align="right">
            <ButtonGroup
              variant="contained"
              aria-label="outlined primary button group"
            >
              {userInfo?.isAdmin && publicFlag && !allFlag && <Button
                component={Link}
                color="yellow"
                aria-label="merge"
                onClick={() => handleOpenMerge(object.id, object.name)}
              >
                <MergeTypeIcon />
              </Button>}
              {userInfo?.isAdmin && !publicFlag && <Button
                component={Link}
                color="yellow"
                aria-label="make public"
                onClick={() => handleMakePublic(object.id)}
              >
                <PublicIcon />
              </Button>}
              <Button
                color="primary"
                aria-label="edit"
                onClick={() => {
                  navigate(`/object/${object.id}`, { state: { fromTab: tab } })
                }}
              >
                <EditIcon />
              </Button>
              {(userInfo?.isAdmin || object.ownerId === userInfo?.ownerId) && <Button
                component={Link}
                onClick={() => handleOpen(object.id)}
                color="error"
                aria-label="delete"
              >
                <DeleteIcon />
              </Button>}
            </ButtonGroup>
          </TableCell>
        </TableRow>
      );
    });
  }, [objects]);

  return (
    <Box>
      <SearchField onChange={(value) => handleSearch(value)} />
      <Box sx={{ width: "100%", height: "100%", margin: 0, padding: 0, marginTop: 1 }}>
        <LazyLoader
          onLoad={() => setPage(prev => prev + 1)}
          loading={isLoading}
          endReached={endReached}
        >
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  {userInfo.isAdmin && <TableCell>ID</TableCell>}
                  <TableCell><FormattedMessage id="Object.name" /></TableCell>
                  <TableCell align="right"><FormattedMessage id="Overview.actions" /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{objectList}</TableBody>
            </Table>
          </TableContainer>
        </LazyLoader>
      </Box>
      {dialog}
      {mergeDialog}
    </Box>
  );
};
